import type { FeatureToggles } from '@makemydeal/dr-platform-shared';
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type { Middleware, MiddlewareAPI } from 'redux';

import { bootstrapActionTypes } from '@makemydeal/dr-shared-store';
import { scopedStateSelectors } from '@makemydeal/shared-scoped-store';
import * as configSelectors from '@makemydeal/dr-dash-store/selectors/config';
import * as dealValidationSelectors from '@makemydeal/dr-dash-store/selectors/dealValidationSelectors';
import * as avoSelectors from '@makemydeal/dr-dash-store/selectors/avoSelectors';
import * as deskingActionCreators from '@makemydeal/dr-dash-store/actions/deskingActionCreators';
import type { StateTree } from '@makemydeal/dr-dash-types';
import { payloadUtils } from '@makemydeal/dr-shared-ui-utils';

import type { ConfigChangeVehicle, MiddlewareManager } from '@makemydeal/dr-activities-change-vehicle';
import { APP_NAME } from '../../../../constants';

// config
import { configureChangeVehicle } from './config';

import { mvDashabordAdditionalSelectors, openDealAdditionalSelectors } from './additionalSelectors';
import { managerViewHostActions, openDealHostActions } from './hostActions';

// keep a cached version of the middleware once we're able to build it.
// exported to support testing
export let cachedChangeVehicleMiddlewareMgr: MiddlewareManager | undefined;

// needed for testing
export const changeVehicleMiddlewareClearCache = () => (cachedChangeVehicleMiddlewareMgr = undefined);

export const changeVehicleMiddleware: Middleware<unknown, StateTree> =
    (store: MiddlewareAPI) => (next: any) => (action: AnyFSA) => {
        switch (action.type) {
            case bootstrapActionTypes.BOOTSTRAP_SUCCESS: {
                // get what we can from state.  This typically will be the bff endpoint only since its placed as part of initial state
                const state = store.getState();
                const bff = configSelectors.getServicesBff(state);
                const dealValidation = dealValidationSelectors.getDealValidationState(state);
                const featureToggles: FeatureToggles = payloadUtils.getFeatureTogglesFromInitPayload(action.payload);
                const { mvDealValidation } = state.featureToggles;
                const isDealValidOrValidationDisabled =
                    !mvDealValidation || (mvDealValidation && dealValidation.valid) || Object.keys(dealValidation).length === 0;

                const initConfig: ConfigChangeVehicle = {
                    applicationName: APP_NAME,
                    services: {
                        bff
                    },
                    featureToggles
                };

                cachedChangeVehicleMiddlewareMgr = configureChangeVehicle(
                    initConfig,
                    isDealValidOrValidationDisabled
                        ? {
                              ...mvDashabordAdditionalSelectors,
                              // Bound here (needs the live `store`). With F291824 ON, aiMiddleware skips the
                              // INIT_SUCCESS fetch, so this screen-open trigger is the sole populator of AVO recs.
                              dispatchFetchIfStale: () => {
                                  // `store` is bound to the primary scope, but its ScopedState carries the real-state
                                  // fields, so resolve the current (navigation) scope from it and seed the fetch there on
                                  // Change-Vehicle-open, so recs are per-scenario and the current-scope vehicle exclusion
                                  // already lines up. (undefined = primary → deal-level.)
                                  const realState = store.getState() as StateTree;
                                  const primaryScopeId = scopedStateSelectors.getPrimaryScopeId(realState as any);
                                  const navScopeId = (realState as any).__navigationScopeTarget || primaryScopeId;
                                  const isPrimaryScope = navScopeId === primaryScopeId;
                                  const scopeState = (scopedStateSelectors.getScopeStateById(realState as any, navScopeId) ??
                                      realState) as StateTree;

                                  if (
                                      mvDashabordAdditionalSelectors.isAiAltVehicleOptionsSectionVisible!(scopeState) &&
                                      avoSelectors.shouldFetchVehicleRecommendations(scopeState)
                                  ) {
                                      // A non-primary scope seeds off its own alternate offer; a primary scope seeds off
                                      // the deal-level offer (undefined seed). Always route the result to the viewed scope
                                      // explicitly: `store` is bound to the scope that was primary when this middleware was
                                      // configured, so an undefined routing arg would land the SUCCESS on that stale scope
                                      // after a make-primary change, not on the scenario actually being viewed.
                                      const seedScopeArg = isPrimaryScope ? undefined : navScopeId;
                                      store.dispatch(
                                          deskingActionCreators.fetchVehicleRecommendations(seedScopeArg, navScopeId) as any
                                      );
                                  }
                              }
                          }
                        : openDealAdditionalSelectors,
                    isDealValidOrValidationDisabled ? managerViewHostActions : openDealHostActions
                );

                break;
            }
        }

        if (cachedChangeVehicleMiddlewareMgr) {
            cachedChangeVehicleMiddlewareMgr.middleware(store)(next)(action);
        } else {
            next(action);
        }
    };
